import React from 'react';
import { Input } from 'antd';
import { Moment } from 'moment';
import {
	Currencies,
	UnnamedBankAccountName,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import DatePicker from '@client/components/DatePicker';
import Select from '@client/components/Select';
import styles from './styles/voyageInvoiceForm.module.css';

export const getTitleSetting = (
	invoiceTitle: string,
	setInvoiceTitle: (title: string) => void,
	cumulative = false,
) => ({
	hide: cumulative,
	label: 'Title',
	content: (
		<Input
			value={invoiceTitle}
			onChange={(e) => setInvoiceTitle(e.target.value)}
		/>
	),
});

export const getShowPaymentTermsSetting = (
	showPaymentTerms: boolean,
	setShowPaymentTerms: (show: boolean) => void,
) => ({
	label: 'Include payment term',
	content: (
		<Select
			value={showPaymentTerms}
			onChange={(v) => typeof v === 'boolean' && setShowPaymentTerms(v)}
			options={[
				{ label: 'Include', value: true },
				{ label: 'Do not include', value: false },
			]}
		/>
	),
});

export const getShowOnBehalfOfSetting = (
	showOnBehalfOf: boolean,
	setShowOnBehalfOf: (show: boolean) => void,
) => ({
	label: 'On behalf of',
	content: (
		<Select
			value={showOnBehalfOf}
			onChange={(v) => typeof v === 'boolean' && setShowOnBehalfOf(v)}
			options={[
				{ label: 'Show', value: true },
				{ label: 'Hide', value: false },
			]}
		/>
	),
});

export const getInvoiceDateSetting = (
	invoiceDate: Moment | undefined,
	setInvoiceDate: (date: Moment) => void,
	cumulative = false,
) => ({
	hide: cumulative,
	label: 'Invoice Date',
	content: (
		<DatePicker
			allowClear={false}
			showNow={false}
			className={styles.settingsInput}
			value={invoiceDate}
			onChange={(date) => setInvoiceDate(date as Moment)}
		/>
	),
});

export const getDueDateSetting = (
	dueDate: Moment | undefined,
	setDueDate: (date: Moment) => void,
	cumulative = false,
) => ({
	hide: cumulative,
	label: 'Due Date',
	content: (
		<DatePicker
			allowClear={false}
			showNow={false}
			className={styles.settingsInput}
			value={dueDate}
			onChange={(date) => setDueDate(date as Moment)}
		/>
	),
});

export const getBankAccountSetting = ({
	bankAccounts,
	currency,
	selectedBankAccount,
	setSelectedBankAccount,
	allowNoBankAccount = false,
}: {
	bankAccounts: Array<{
		id: number;
		name: string | null;
		currency: Values<typeof Currencies>;
		vesselId: number | null;
		businessUnit: string | null;
	}>;
	currency: Values<typeof Currencies>;
	selectedBankAccount: number;
	setSelectedBankAccount: (bankAccountId: number) => void;
	allowNoBankAccount?: boolean;
}) => {
	const orgBankAccounts = bankAccounts
		.filter((account) => account?.vesselId === null)
		.map((account) => ({
			label: `${account.name || UnnamedBankAccountName} (${account.currency})`,
			value: account.id,
		}));

	const vesselBankAccounts = bankAccounts
		.filter((account) => account?.vesselId != null)
		.map((account) => {
			const label = account.name || UnnamedBankAccountName;
			const suffix = account.businessUnit != null ?
				`(${account.businessUnit}) (${account.currency})` :
				`(${account.currency})`;

			return {
				label: `${label} ${suffix}`,
				value: account.id,
			};
		});

	const noAvailableAccounts = orgBankAccounts.length === 0 && vesselBankAccounts.length === 0;

	return {
		label: 'Bank Account',
		content: (
			noAvailableAccounts ? (
				<strong>
					No available bank accounts with currency
					{' '}
					{currency}
				</strong>
			) : (
				<Select
					value={selectedBankAccount}
					onSelect={(v) => setSelectedBankAccount(v as number)}
					options={[
						...(orgBankAccounts.length > 0 ? [{
							label: 'Organization',
							options: [
								...orgBankAccounts,
							],
						}] : []),
						...(vesselBankAccounts.length > 0 ? [{
							label: 'Vessel',
							options: [
								...vesselBankAccounts,
							],
						}] : []),
						...(allowNoBankAccount ? [{
							label: 'No bank account',
							value: -1,
						}] : []),
					]}
					showSearch
				/>
			)

		),
	};
};
